<script setup lang="ts">
  import { cva } from 'class-variance-authority'
  import { getShortDateForSpecificLocale, CAMPAIGN_ARIA_DATE_FORMAT } from '@/utils/constants/date'
  import type { TClpBanner } from '@/components/campaign/banner/types'

  const props = defineProps<{ data: TClpBanner }>()

  const counterBase = computed(() =>
    cva('w-full md:h-120 xl:h-140', {
      variants: {
        theme: {
          green: 'text-bt-blue-dark bg-bt-green',
          purple: 'text-white bg-bt-grabout-violet',
          blue: 'text-white bg-bt-blue-ui'
        }
      }
    })({
      theme: props.data.color_theme || 'green'
    })
  )

  const { isDesktop } = useBTBreakpoints()

  const dayjs = useDayjs()

  const showTimer = computed(() => props.data.show_timer)

  const { getTitleContent } = useDynamicClpBannerTitle()
  const title = getTitleContent(props.data)
</script>
<template>
  <div :class="counterBase">
    <div v-if="!isDesktop">
      <slot />
    </div>
    <div class="container grid h-full justify-between px-2 pt-10 md:grid-cols-12 md:flex-row md:pt-0">
      <div class="col-span-5 flex flex-col items-center self-center md:items-start">
        <h1 class="mb-6 w-full text-left sm:text-center md:text-left">
          {{ title }}
        </h1>
        <div class="flex w-full items-center justify-between gap-5 sm:w-fit sm:justify-normal sm:gap-10">
          <div class="flex flex-col items-start sm:items-center md:items-start">
            <p class="mb-2 text-subtitle-1">{{ $t('book') }}</p>
            <div class="flex flex-col sm:flex-row md:flex-col">
              <LazyCampaignBannerDateRange
                :start-date="dayjs(data.book_date_start).format(getShortDateForSpecificLocale($i18n.locale))"
                :end-date="dayjs(data.book_date_end).format(getShortDateForSpecificLocale($i18n.locale))"
                :start-date-semantic="dayjs(data.book_date_start).format(CAMPAIGN_ARIA_DATE_FORMAT)"
                :end-date-semantic="dayjs(data.book_date_end).format(CAMPAIGN_ARIA_DATE_FORMAT)"
              />
            </div>
          </div>
          <LazyCampaignBannerFakeButton />
          <div class="flex flex-col items-start sm:items-center md:items-start">
            <p class="mb-2 text-subtitle-1">{{ $t('travel') }}</p>
            <div class="flex flex-col sm:flex-row md:flex-col">
              <LazyCampaignBannerDateRange
                :start-date="dayjs(data.travel_date_start).format(getShortDateForSpecificLocale($i18n.locale))"
                :end-date="dayjs(data.travel_date_end).format(getShortDateForSpecificLocale($i18n.locale))"
                :start-date-semantic="dayjs(data.travel_date_start).format(CAMPAIGN_ARIA_DATE_FORMAT)"
                :end-date-semantic="dayjs(data.travel_date_end).format(CAMPAIGN_ARIA_DATE_FORMAT)"
              />
            </div>
          </div>
        </div>
        <div
          v-if="showTimer"
          class="relative mt-24 md:mt-10"
        >
          <div class="-md:top-4 absolute -top-20 flex min-h-20 w-full justify-center md:relative md:top-0 md:min-h-24">
            <CampaignCounter
              :start-date="data.book_date_start"
              :end-date="data.book_date_end"
            />
          </div>
        </div>
      </div>
      <div
        v-if="isDesktop"
        class="col-start-8 col-end-13 overflow-hidden rounded-t-full md:mt-12"
      >
        <slot />
      </div>
    </div>
  </div>
</template>
